import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:   <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:  <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    ArrowUpRight:<><path d="M7 7h10v10"/><path d="M7 17 17 7"/></>,
    Mail:        <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    Newspaper:   <><path d="M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/><path d="M8 7h8M8 11h8M8 15h4"/></>,
    Megaphone:   <><path d="m3 11 19-9-9 19-2-8-8-2z"/></>,
    Globe:       <><circle cx="12" cy="12" r="10"/><line x1="2" x2="22" y1="12" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    Calendar:    <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    Tag:         <><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/></>,
    Sparkles:    <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
    Rss:         <><path d="M4 11a9 9 0 0 1 9 9"/><path d="M4 4a16 16 0 0 1 16 16"/><circle cx="5" cy="19" r="1"/></>,
    Download:    <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></>,
    Filter:      <><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></>,
    CheckCircle: <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Content data ── */
const ANNOUNCEMENTS = [
  {
    id: "a1",
    date: "June 2026",
    tag: "Product",
    title: "JiraniApp launches real-time job tracking across all bookings",
    summary: "Clients can now follow their JiraniPro from departure to arrival and through each stage of the job, directly inside the app. The update also introduces milestone-based status updates so both clients and professionals have a shared view of progress.",
    featured: true,
  },
  {
    id: "a2",
    date: "May 2026",
    tag: "Expansion",
    title: "JiraniApp goes live in Kisumu and Nakuru",
    summary: "Following strong demand from users outside Nairobi, JiraniApp has formally launched in Kisumu and Nakuru. Both cities now have verified professionals available for booking across cleaning, plumbing, electrical, and other core home services.",
    featured: false,
  },
  {
    id: "a3",
    date: "April 2026",
    tag: "Trust and Safety",
    title: "Enhanced background verification now live for all new JiraniPros",
    summary: "All new professionals joining JiraniApp now go through an upgraded verification process that includes a national ID check, a criminal background check, and a reference review. Existing professionals are being migrated to the new standard on a rolling basis.",
    featured: false,
  },
  {
    id: "a4",
    date: "March 2026",
    tag: "Product",
    title: "In-app messaging and booking history now available to all users",
    summary: "Two highly requested features are now live for all JiraniApp users. In-app messaging allows direct, documented communication between clients and professionals before and during a booking. Booking history gives clients a full view of past jobs, payments, and reviews in one place.",
    featured: false,
  },
  {
    id: "a5",
    date: "February 2026",
    tag: "Community",
    title: "Introducing the JiraniPro Excellence Awards — recognising outstanding professionals",
    summary: "JiraniApp has launched its first annual Excellence Awards programme to formally recognise JiraniPros who consistently deliver exceptional work, maintain high ratings, and embody the values of the platform. Nominations open to all registered clients.",
    featured: false,
  },
  {
    id: "a6",
    date: "January 2026",
    tag: "Expansion",
    title: "JiraniApp officially launches in Mombasa",
    summary: "After a successful pilot period, JiraniApp is now fully live in Mombasa with a growing network of verified professionals. The launch covers Nyali, Bamburi, Tudor, Likoni, and surrounding areas, with more neighbourhoods being added as the network grows.",
    featured: false,
  },
];

const PRESS = [
  {
    id: "p1",
    publication: "Business Daily Africa",
    date: "May 2026",
    title: "How JiraniApp is bringing accountability to Kenya's informal home services sector",
    summary: "A deep look at how JiraniApp's verification model and payment infrastructure is changing client expectations and creating new income stability for independent home service professionals across Nairobi.",
    url: "#",
    logo: "BD",
    logoColor: "#1d4ed8",
    logoBg: "#eff6ff",
  },
  {
    id: "p2",
    publication: "TechCabal",
    date: "April 2026",
    title: "JiraniApp's hyperlocal approach is what sets it apart from regional competitors",
    summary: "TechCabal profiles JiraniApp's neighbourhood-first matching model and examines how prioritising proximity over volume has driven higher satisfaction rates and lower dispute rates compared to broader marketplace approaches.",
    url: "#",
    logo: "TC",
    logoColor: "#7e22ce",
    logoBg: "#fdf4ff",
  },
  {
    id: "p3",
    publication: "The Standard",
    date: "March 2026",
    title: "Nairobi residents are turning to verified platforms for home services after years of word-of-mouth",
    summary: "The Standard explores the shift in consumer behaviour as Nairobi residents increasingly seek verified, reviewed professionals over informal referrals, citing trust, payment security, and accountability as key drivers.",
    url: "#",
    logo: "TS",
    logoColor: "#c2410c",
    logoBg: "#fff7ed",
  },
  {
    id: "p4",
    publication: "Disrupt Africa",
    date: "February 2026",
    title: "JiraniApp raises seed funding to accelerate expansion across East African cities",
    summary: "Disrupt Africa reports on JiraniApp's seed round, with plans to grow the professional network in Mombasa, Kisumu, and Nakuru and invest in product infrastructure including the payments layer and identity verification pipeline.",
    url: "#",
    logo: "DA",
    logoColor: "#15803d",
    logoBg: "#f0fdf4",
  },
  {
    id: "p5",
    publication: "Nation Africa",
    date: "January 2026",
    title: "The app helping Kenyans find and trust home service professionals",
    summary: "Nation Africa features JiraniApp as part of a wider feature on technology solutions addressing everyday Kenyan challenges, focusing on the platform's M-Pesa integration and phone-first approach to account creation.",
    url: "#",
    logo: "NA",
    logoColor: "#92400e",
    logoBg: "#fffbeb",
  },
];

const NEWS = [
  {
    id: "n1",
    date: "June 2026",
    tag: "Platform",
    title: "Q2 2026: JiraniApp by the numbers",
    summary: "A look at the platform's growth through the second quarter of 2026, including bookings completed, new professionals onboarded, cities active, and satisfaction rates across all service categories.",
  },
  {
    id: "n2",
    date: "May 2026",
    tag: "Professionals",
    title: "Meet the JiraniPros: stories from the platform",
    summary: "We spoke to five JiraniPros across Nairobi and Mombasa about how the platform has changed the way they work, how they manage bookings, and what consistent, reviewable work has meant for their income and reputation.",
  },
  {
    id: "n3",
    date: "April 2026",
    tag: "Payments",
    title: "How JiraniApp's payment model protects both clients and professionals",
    summary: "An explainer on how the platform's payment flow works, why funds are held until job completion, how the payout timeline for JiraniPros operates, and what the Jirani Guarantee covers in practice.",
  },
  {
    id: "n4",
    date: "March 2026",
    tag: "Safety",
    title: "What the Jirani Guarantee actually covers — a full breakdown",
    summary: "A detailed walkthrough of the Jirani Guarantee: what qualifies for protection, how to raise a dispute, what evidence helps a case, and how our Trust and Safety team approaches resolution.",
  },
  {
    id: "n5",
    date: "February 2026",
    tag: "Community",
    title: "Building trust in home services: lessons from six months of operation",
    summary: "Reflections on what we have learned about trust, reliability, and the professional-client relationship from the platform's first six months of active bookings across Nairobi.",
  },
  {
    id: "n6",
    date: "January 2026",
    tag: "Product",
    title: "Why we built JiraniApp for phone numbers, not email addresses",
    summary: "A look at the product decisions behind JiraniApp's phone-first account model, M-Pesa payment integration, and why designing for how Kenyans actually use technology shaped every major feature decision.",
  },
];

const TAG_COLORS = {
  "Product":        { color: T.g700,    bg: T.g50,      border: T.g100 },
  "Expansion":      { color: "#1d4ed8", bg: "#eff6ff",  border: "#bfdbfe" },
  "Trust and Safety":{ color: "#7e22ce", bg: "#fdf4ff", border: "#e9d5ff" },
  "Community":      { color: "#c2410c", bg: "#fff7ed",  border: "#fed7aa" },
  "Professionals":  { color: "#0f766e", bg: "#f0fdfa",  border: "#99f6e4" },
  "Payments":       { color: "#92400e", bg: "#fffbeb",  border: "#fde68a" },
  "Safety":         { color: T.g700,    bg: T.g50,      border: T.g100 },
  "Platform":       { color: T.n700,    bg: T.n50,      border: T.n150 },
};

function TagPill({ tag }) {
  const style = TAG_COLORS[tag] || { color: T.n600, bg: T.n50, border: T.n150 };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, letterSpacing: "0.05em",
      color: style.color, background: style.bg,
      border: `1px solid ${style.border}`,
      borderRadius: 99, padding: "3px 10px",
    }}>{tag}</span>
  );
}

/* ── Featured announcement ── */
function FeaturedCard({ item }) {
  return (
    <div style={{
      padding: "clamp(24px,4vw,36px)", borderRadius: 20,
      background: T.g50, border: `1.5px solid ${T.g100}`,
      marginBottom: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.g600, letterSpacing: "0.08em", textTransform: "uppercase", background: T.white, border: `1px solid ${T.g200}`, borderRadius: 99, padding: "3px 10px" }}>
          Latest
        </span>
        <TagPill tag={item.tag} />
        <span style={{ fontSize: 12, color: T.n400, display: "flex", alignItems: "center", gap: 4 }}>
          <Icon name="Calendar" size={12} color={T.n400} strokeWidth={2} />
          {item.date}
        </span>
      </div>
      <h3 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
        {item.title}
      </h3>
      <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: 0 }}>{item.summary}</p>
    </div>
  );
}

/* ── Standard announcement card ── */
function AnnouncementCard({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "22px", borderRadius: 16,
        border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        transition: "all .15s", cursor: "default",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <TagPill tag={item.tag} />
        <span style={{ fontSize: 12, color: T.n400, display: "flex", alignItems: "center", gap: 4 }}>
          <Icon name="Calendar" size={11} color={T.n400} strokeWidth={2} />
          {item.date}
        </span>
      </div>
      <h3 style={{ fontFamily: T.serif, fontSize: "clamp(17px,2.5vw,20px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.3 }}>
        {item.title}
      </h3>
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: 0 }}>{item.summary}</p>
    </div>
  );
}

/* ── Press mention card ── */
function PressCard({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "block", padding: "22px", borderRadius: 16, textDecoration: "none",
        border: `1.5px solid ${hovered ? T.n300 : T.n150}`,
        background: hovered ? T.n50 : T.white,
        transition: "all .15s",
      }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        {/* Publication logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: item.logoBg, display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 12, fontWeight: 700,
            color: item.logoColor, fontFamily: T.sans, letterSpacing: "0.02em",
          }}>
            {item.logo}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n800 }}>{item.publication}</div>
            <div style={{ fontSize: 12, color: T.n400, display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
              <Icon name="Calendar" size={11} color={T.n400} strokeWidth={2} />
              {item.date}
            </div>
          </div>
        </div>
        <Icon name="ArrowUpRight" size={16} color={hovered ? T.n700 : T.n300} strokeWidth={2} />
      </div>
      <h3 style={{ fontFamily: T.serif, fontSize: "clamp(16px,2.5vw,19px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.02em", lineHeight: 1.3 }}>
        {item.title}
      </h3>
      <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.65, margin: 0 }}>{item.summary}</p>
    </a>
  );
}

/* ── News card ── */
function NewsCard({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "20px", borderRadius: 14,
        border: `1.5px solid ${hovered ? T.n200 : T.n100}`,
        background: hovered ? T.n50 : T.white,
        transition: "all .15s",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <TagPill tag={item.tag} />
        <span style={{ fontSize: 12, color: T.n400 }}>{item.date}</span>
      </div>
      <h3 style={{ fontFamily: T.serif, fontSize: "clamp(16px,2.5vw,19px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.02em", lineHeight: 1.3 }}>
        {item.title}
      </h3>
      <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.65, margin: 0 }}>{item.summary}</p>
    </div>
  );
}

/* ── Section heading ── */
function SectionHead({ eyebrow, title, em, sub }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
        {eyebrow}
      </span>
      <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
        {title} <em style={{ fontStyle: "italic", color: T.g600 }}>{em}</em>
      </h2>
      {sub && <p style={{ fontSize: 15, color: T.n400, margin: 0, lineHeight: 1.65 }}>{sub}</p>}
    </div>
  );
}

/* ── Tab bar ── */
function TabBar({ active, onChange, tabs }) {
  return (
    <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.n150}`, marginBottom: 28 }}>
      {tabs.map(tab => (
        <button key={tab.id} onClick={() => onChange(tab.id)} style={{
          padding: "10px 18px", background: "none", border: "none",
          borderBottom: `2px solid ${active === tab.id ? T.g500 : "transparent"}`,
          cursor: "pointer", fontFamily: T.sans, fontSize: 14,
          fontWeight: active === tab.id ? 600 : 400,
          color: active === tab.id ? T.n900 : T.n400,
          marginBottom: -1, transition: "all .15s",
        }}>
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ══ Main page ══ */
export default function PressAndNewsPage() {
  useFonts();
  const navigate = useNavigate();
  const [tab, setTab] = useState("announcements");

  const TABS = [
    { id: "announcements", label: "Announcements" },
    { id: "press",         label: "Press coverage" },
    { id: "news",          label: "News and stories" },
  ];

  const featured = ANNOUNCEMENTS.find(a => a.featured);
  const rest     = ANNOUNCEMENTS.filter(a => !a.featured);

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`* { box-sizing: border-box; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}` }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      {/* ── Hero ── */}
      <div style={{ padding: "clamp(48px,7vw,88px) 20px clamp(40px,5vw,60px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <Icon name="Newspaper" size={13} color={T.g500} strokeWidth={2} />
            Press and news
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,62px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.07, color: T.n900, letterSpacing: "-0.03em", maxWidth: 560 }}>
            What is happening at{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>JiraniApp</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 32px", maxWidth: 520 }}>
            Company announcements, product updates, press coverage, and stories from across
            the platform. Everything you need to stay current with JiraniApp.
          </p>

          {/* Press contact pill */}
          <a href="mailto:press@jiraniapp.com" style={{
            display: "inline-flex", alignItems: "center", gap: 9,
            padding: "11px 20px", borderRadius: 10,
            border: `1.5px solid ${T.n150}`, background: T.n50,
            textDecoration: "none", color: T.n700,
            fontSize: 13, fontWeight: 600, fontFamily: T.sans,
            transition: "all .15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.g200; e.currentTarget.style.background = T.g50; e.currentTarget.style.color = T.g700; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n150; e.currentTarget.style.background = T.n50; e.currentTarget.style.color = T.n700; }}>
            <Icon name="Mail" size={15} strokeWidth={1.8} />
            Press enquiries: press@jiraniapp.com
          </a>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>
        <div style={{ paddingTop: "clamp(36px,5vw,52px)" }}>

          {/* ── Stats row ── */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: "clamp(40px,6vw,56px)" }}>
            {[
              { num: "4",    label: "Cities active" },
              { num: "700+", label: "Verified professionals" },
              { num: "2026", label: "Founded" },
              { num: "Kenya", label: "Headquartered" },
            ].map(s => (
              <div key={s.label} style={{ flex: "1 1 120px", padding: "16px 18px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.n50 }}>
                <div style={{ fontFamily: T.serif, fontSize: "clamp(20px,3vw,26px)", fontWeight: 400, color: T.n900, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 4 }}>{s.num}</div>
                <div style={{ fontSize: 12, color: T.n400 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Tabs ── */}
          <TabBar active={tab} onChange={setTab} tabs={TABS} />

          {/* ── Announcements ── */}
          {tab === "announcements" && (
            <div>
              {featured && <FeaturedCard item={featured} />}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                {rest.map(a => <AnnouncementCard key={a.id} item={a} />)}
              </div>
            </div>
          )}

          {/* ── Press ── */}
          {tab === "press" && (
            <div>
              <SectionHead
                eyebrow="In the media"
                title="JiraniApp in the"
                em="press"
                sub="Coverage from publications across Kenya and the African tech ecosystem."
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {PRESS.map(p => <PressCard key={p.id} item={p} />)}
              </div>

              {/* Press kit */}
              <div style={{ marginTop: 40, padding: "clamp(22px,4vw,32px)", borderRadius: 16, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: T.white, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="Download" size={18} color={T.n400} strokeWidth={1.7} />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 5 }}>Press kit</div>
                  <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.65, margin: "0 0 14px" }}>
                    Download our press kit for official logos, brand assets, platform screenshots,
                    company facts, and founder bios for editorial use.
                  </p>
                  <a href="mailto:press@jiraniapp.com" style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    padding: "9px 18px", background: T.white,
                    border: `1.5px solid ${T.n200}`, borderRadius: 9,
                    textDecoration: "none", color: T.n700,
                    fontSize: 13, fontWeight: 600, fontFamily: T.sans,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = T.n400)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = T.n200)}>
                    <Icon name="Mail" size={14} strokeWidth={1.8} />
                    Request press kit
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ── News and stories ── */}
          {tab === "news" && (
            <div>
              <SectionHead
                eyebrow="News and stories"
                title="From the"
                em="platform"
                sub="Platform updates, professional stories, and deeper reads on how JiraniApp works."
              />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                {NEWS.map(n => <NewsCard key={n.id} item={n} />)}
              </div>
            </div>
          )}

          {/* ── Newsletter signup ── */}
          <div style={{ marginTop: "clamp(48px,7vw,72px)", padding: "clamp(28px,5vw,40px)", borderRadius: 20, background: T.g50, border: `1px solid ${T.g100}` }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 14 }}>
              <Icon name="Rss" size={13} color={T.g500} strokeWidth={2} />
              Stay updated
            </div>
            <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,28px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
              Get JiraniApp news by <em style={{ fontStyle: "italic", color: T.g600 }}>email</em>
            </h2>
            <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: "0 0 20px" }}>
              New city launches, product updates, and platform stories sent directly to you.
              No more than twice a month.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", maxWidth: 460 }}>
              <input
                type="email"
                placeholder="Your email address"
                style={{
                  flex: 1, minWidth: 200, padding: "12px 14px",
                  border: `1.5px solid ${T.g200}`, borderRadius: 10,
                  fontSize: 14, fontFamily: T.sans, color: T.n900,
                  background: T.white, outline: "none", boxSizing: "border-box",
                }}
                onFocus={e => (e.target.style.borderColor = T.g500)}
                onBlur={e => (e.target.style.borderColor = T.g200)}
              />
              <button style={{
                padding: "12px 22px", background: T.g500, color: T.white,
                border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", whiteSpace: "nowrap",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
              onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
                Subscribe
              </button>
            </div>
            <p style={{ fontSize: 12, color: T.g600, margin: "10px 0 0" }}>
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>

          {/* ── Bottom CTA ── */}
          <div style={{ marginTop: "clamp(40px,6vw,56px)", padding: "clamp(28px,5vw,44px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
            <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,34px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
              Experience JiraniApp{" "}
              <em style={{ fontStyle: "italic", color: T.g600 }}>yourself</em>
            </h2>
            <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
              Read enough? See what the platform looks like from the inside.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => navigate("/search")}
                style={{ padding: "13px 28px", background: T.g500, color: T.white, border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
                onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
                onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
                Find a professional
                <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
              </button>
              <button onClick={() => navigate("/how-it-works")}
                style={{ padding: "13px 28px", background: "transparent", color: T.n700, border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = T.n400)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = T.n200)}>
                How JiraniApp works
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}